import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ShieldCheck, CreditCard, Lock, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { api } from "@/lib/api";

const CURRENCIES = [
  { code: "RUB", symbol: "₽" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "KZT", symbol: "₸" },
  { code: "UAH", symbol: "₴" },
];

export default function PaymentPage() {
  const { lang, t } = useI18n();
  const { content, loading } = useContent();
  const block = content?.payment;
  const h = block ? pick(block, lang, block.ru) : null;
  const defaultCurrency = block?.default_currency || "RUB";
  const [amount, setAmount] = useState(block?.default_amount || "");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Hidden via admin → redirect to home (no broken bookmarks, no SEO bleed).
  if (!loading && content?.visibility?.payment === false) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error(lang === "ru" ? "Заполните имя и email" : "Fill name and email");
      return;
    }
    const numeric = parseFloat(amount);
    if (!numeric || numeric <= 0) {
      toast.error(lang === "ru" ? "Введите сумму больше нуля" : "Enter an amount above zero");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payment/create", {
        amount: numeric,
        currency,
        email: email.trim(),
        name: fullName.trim(),
        note: note.trim() || undefined,
      });
      if (data?.pay_url) {
        toast.success(lang === "ru" ? "Перенаправляем на оплату…" : "Redirecting to payment…");
        window.location.href = data.pay_url;
        return;
      }
      throw new Error("No pay_url");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === "string" && msg
          ? msg
          : (lang === "ru" ? "Не удалось создать платёж. Попробуйте позже." : "Could not create payment. Try later.")
      );
      setSubmitting(false);
    }
  };

  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "";
  const amountDisplay = parseFloat(amount || "0").toLocaleString();

  if (!h) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="payment-back">
            <ArrowLeft className="size-4" /> {t("common.back_home")}
          </Link>

          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12">
            {/* LEFT — description */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-4">
                {h.eyebrow}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-semibold tracking-tight mb-5" data-testid="payment-title">
                {h.title}
              </h1>
              <p className="text-base text-muted-foreground mb-10 max-w-2xl leading-relaxed">
                {h.subtitle}
              </p>

              <h2 className="font-heading text-xl sm:text-2xl font-semibold mb-5">
                {h.checkpoints_title}
              </h2>
              <ul className="space-y-3 mb-8" data-testid="payment-checkpoints">
                {(h.checkpoints || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 rounded-2xl border border-border bg-card">
                    <CheckCircle2 className="size-5 mt-0.5 shrink-0 text-accent" />
                    <span className="text-sm text-foreground/90">{c}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/refund"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors mb-8"
                data-testid="payment-refund-link"
              >
                <RefreshCw className="size-4" />
                {lang === "ru" ? "Политика возврата" : "Refund policy"}
              </Link>

              {h.refund_note ? (
                <div className="text-sm text-muted-foreground mb-6 leading-relaxed">{h.refund_note}</div>
              ) : null}

              {h.info_note ? (
                <div className="p-5 rounded-3xl bg-muted/40 border border-border flex items-start gap-3">
                  <Info className="size-5 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground leading-relaxed">{h.info_note}</div>
                </div>
              ) : null}
            </motion.div>

            {/* RIGHT — payment form */}
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="lg:sticky lg:top-28 p-5 sm:p-7 lg:p-8 rounded-3xl border border-border bg-card" data-testid="payment-panel">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="size-5 text-accent" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    {h.secure_label}
                  </span>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-6">
                  {h.details_title}
                </h2>

                <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
                      {h.name_label} *
                    </label>
                    <input
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full h-12 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid="payment-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-12 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid="payment-email"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
                        {h.amount_label} *
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">{symbol}</span>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          required
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="0"
                          className="w-full h-12 pl-9 pr-4 rounded-full border border-input bg-background text-base font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                          data-testid="payment-amount"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
                        {h.currency_label}
                      </label>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        className="w-full h-12 px-3 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        data-testid="payment-currency"
                      >
                        {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
                      {h.comment_label}
                    </label>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={h.comment_placeholder}
                      className="w-full h-12 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid="payment-note"
                    />
                  </div>

                  <div className="pt-2">
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/40 mb-4">
                      <span className="text-sm text-muted-foreground">{h.total_label}</span>
                      <span className="font-heading text-2xl font-bold">{symbol}{amountDisplay}</span>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-foreground text-background font-semibold text-base hover:translate-y-[-2px] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
                      data-testid="payment-submit"
                    >
                      <CreditCard className="size-5" />
                      {submitting ? (lang === "ru" ? "Создаём платёж…" : "Creating payment…") : h.submit_label}
                    </button>
                    <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                      <Lock className="size-3.5" />
                      {h.secure_note}
                    </div>
                  </div>
                </form>
              </div>
            </motion.aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
